import { useState } from "react";
import {
  Menu,
  MenuItem,
  ButtonProps,
  Avatar,
  Button,
  ListItemIcon,
  styled,
  Tooltip,
} from "@mui/material";
import {
  AccountCircle,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "marvin-auth-kit";

import { useLoginRedirect } from "../../hooks/settingHooks";

import { TokenUser } from "../../types";
import { NavLink } from "../navigation/NavLink";

interface Props extends ButtonProps {
  config: string;
  className?: string;
}

export function AccountMenu(props: Props) {
  const currentUser = useCurrentUser<TokenUser | undefined>();
  const { t } = useTranslation();
  const loginRedirectPath = useLoginRedirect(props.config);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  function handleMenu(event: React.MouseEvent<HTMLElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  return render();

  function render() {
    return (
      <>
        <Tooltip title={`Signed in as ${currentUser?.email || ""}`}>
          <Button className={props.className} onClick={handleMenu} size="large">
            {!currentUser && <AccountCircle />}

            {currentUser && (
              <Avatar
                sx={{
                  width: "28px",
                  height: "28px",
                  textTransform: "uppercase",
                }}
              >
                <span>{currentUser.email.charAt(0)}</span>
              </Avatar>
            )}
          </Button>
        </Tooltip>

        <StyledMenu
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
          keepMounted
          onClose={handleClose}
        >
          {!currentUser && (
            <div>
              <MenuItem disabled>{t("label.publicAccount")}</MenuItem>
              <MenuItem component={NavLink} to={loginRedirectPath}>
                {t("label.login")}
              </MenuItem>
            </div>
          )}
          {currentUser && (
            <div>
              {currentUser.roles.includes("root") && (
                <MenuItem component={NavLink} to={`/${props.config}/admin`}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  {t("label.administratorPanel")}
                </MenuItem>
              )}
              <MenuItem component={NavLink} to={`/${props.config}/app/logout`}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t("label.logout")}
              </MenuItem>
            </div>
          )}
        </StyledMenu>
      </>
    );
  }
}

const StyledMenu = styled(Menu)``;
